import { variantLabel } from "@/lib/format";

export type SalesOrderListLine = {
  productName?: string | null;
  sku?: string | null;
  optionValues?: Record<string, string> | null;
  qty: number;
  bundleParentItemId?: string | null;
};

export type SalesOrderItemSummary = {
  title: string;
  variant: string;
  sku: string;
  qtyLabel: string;
  extraCount: number;
  tooltip: string;
  empty: boolean;
};

function lineSku(line: SalesOrderListLine): string {
  return (line.sku ?? "").trim();
}

function lineTitle(line: SalesOrderListLine): string {
  const name = (line.productName ?? "").trim();
  return name || "Untitled item";
}

function formatLine(line: SalesOrderListLine): string {
  const bits = [lineTitle(line)];
  const variant = variantLabel(line.optionValues);
  if (variant) bits.push(variant);
  const sku = lineSku(line);
  if (sku) bits.push(sku);
  bits.push(`Qty ${line.qty}`);
  return bits.join(" · ");
}

export function sellableSalesOrderLines<T extends { bundleParentItemId?: string | null; bundle_parent_item_id?: string | null }>(
  items: T[],
): T[] {
  return items.filter((it) => !it.bundleParentItemId && !(it as { bundle_parent_item_id?: string | null }).bundle_parent_item_id);
}

export function summarizeSalesOrderItems(items: SalesOrderListLine[]): SalesOrderItemSummary {
  const lines = sellableSalesOrderLines(items);
  if (lines.length === 0) {
    return { title: "—", variant: "", sku: "", qtyLabel: "", extraCount: 0, tooltip: "", empty: true };
  }
  const first = lines[0];
  const extraCount = Math.max(0, lines.length - 1);
  const title = lineTitle(first);
  const variant = variantLabel(first.optionValues);
  const sku = lineSku(first);
  const qtyLabel = `Qty ${first.qty}`;
  const tooltip = lines.map(formatLine).join("\n") + (extraCount > 0 ? `\n+ ${extraCount} more` : "");
  return {
    title,
    variant,
    sku,
    qtyLabel,
    extraCount,
    tooltip: extraCount > 0 ? `${formatLine(first)}\n+ ${extraCount} more` : formatLine(first),
    empty: false,
  };
}

export function salesOrderItemSummaryHeadline(summary: SalesOrderItemSummary): string {
  if (summary.empty) return "—";
  if (summary.extraCount > 0) return `${summary.title} + ${summary.extraCount} more`;
  return summary.title;
}
